import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { getWeeklyEffectList } from '@op/services';
import { useEffect, useMemo, useState } from 'react';
import { FilterData, SortLoadingKeysType, Tags } from '../type.d';
import { DBO_TM } from '@op/utils/format';
import useTabHistory from '@op/hooks/useTabHistory';
import { useSortLoading } from '@op/models/commonLoading/useSortLoading'; 

interface VersionList {
  id: string;
  label: string;
  timeList: {
    text: string;
    value: string;
    filter: { start_time: string; end_time: string };
    moduleList: {
      text: string;
      label: string;
      value: string;
      id: string;
      moduleId: number;
      keywords: string[][];
      tags: Tags[];
    }[];
  }[];
}

export function useFilterData({ gameInfo }: { gameInfo: any }) {
  const { wrapperCancelRequest, loading, setLoading } = useCancelRequestHook({
    defaultLoading: true,
  });
  const [versionList, setVersionList] = useState<VersionList[]>([]);
  const [effectiveTime, setEffectiveTime] = useState<string>();
  // 版本tab被选中项
  const { tab, onActive } = useTabHistory<string>({ defaultValue: '0' });
  const { initLoading } = useSortLoading<SortLoadingKeysType>({
    keys: ['metric', 'charts', 'insight', 'execute'],
  });

  function setDefaultValue(defaultValue?: VersionList) {
    setEffectiveTime(defaultValue?.timeList?.[0]?.value);
  }

  function changeTabActive(tabInfo: { id: string }) {
    setLoading(true);
    onActive(tabInfo);
    setDefaultValue(versionList.find(item => item.id === tabInfo.id));

    setTimeout(() => {
      setLoading(false);
    }, 100);
  }

  // 1. 获取数据
  useEffect(() => {
    wrapperCancelRequest(getWeeklyEffectList, {
      // game_id待删
      game_id: gameInfo.edition_unified_id,
      edition_unified_id: gameInfo.edition_unified_id,
    })
      .then((data: FilterData[]) => {
        if (data?.length > 0) {
          // 初始化版本tab
          const versionList = data.map(item => {
            const list = (item?.times || []).map(it => {
              const text = DBO_TM.timeTitle({ ...it, date_type: 'weekly' });
              return {
                text,
                value: text + item.version_id,
                filter: { start_time: it.start_time, end_time: it.end_time },
                moduleList: (it.modules || []).map(info => {
                  return {
                    text: info.module_name,
                    label: info.module_name,
                    value: String(info.module_id),
                    id: String(info.module_id),
                    moduleId: info.module_id,
                    keywords: info.keywords || [],
                    tags: info.tags || [],
                  };
                }),
              };
            });

            return {
              id: String(item.version_id),
              label: item.version_name,
              timeList: list,
            };
          });
          setVersionList(versionList);
          const defaultSelectItem = versionList[0] || {};
          onActive({ id: defaultSelectItem?.id });
          setDefaultValue(defaultSelectItem);
        }
      })
      .catch(console.error);
  }, []);

  // 时间筛选器选项
  const timeSelectOptions = useMemo(() => {
    const version = versionList.find(item => item.id === tab);
    if (!version) return [];
    return version.timeList;
  }, [tab, versionList]);

  // 模块的选项
  const { moduleOptions, allKeys } = useMemo(() => {
    const timeSelect = timeSelectOptions.find(
      item => item.value === effectiveTime,
    );

    if (!timeSelect) return { moduleOptions: [], allKeys: [] };

    const options = timeSelect.moduleList;
    const allKeys = options.map(item => item.value);
    initLoading(allKeys);
    return {
      moduleOptions: options,
      allKeys,
    };
  }, [timeSelectOptions, effectiveTime]);

  const filter = useMemo(() => {
    if (effectiveTime === undefined) return null;
    const item = timeSelectOptions.find(item => item.value === effectiveTime);
    if (!item) return null;

    return {
      ...gameInfo,
      ...(item.filter || {}),
      version_id: Number(tab),
      date_type: 'weekly',
    };
  }, [effectiveTime, timeSelectOptions]);

  return {
    versionList,
    loading,
    moduleOptions,
    timeSelectOptions,
    effectiveTime,
    setEffectiveTime,
    tab,
    changeTabActive,
    filter,
    allKeys,
  };
}
